import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

type AuthStatus = 'checking' | 'authenticated' | 'unauthenticated'

type AuthContextValue = {
  status: AuthStatus
  username: string | null
  login: (username: string, password: string) => Promise<void>
  logout: () => Promise<void>
}

type AuthPayload = {
  data?: { authenticated?: boolean; username?: string | null }
  error?: { message?: string }
}

const AuthContext = createContext<AuthContextValue | null>(null)

async function authRequest(path: string, init?: RequestInit) {
  const response = await fetch(path, {
    ...init,
    credentials: 'same-origin',
    headers: {
      ...(init?.body ? { 'content-type': 'application/json' } : {}),
      ...(init?.headers ?? {}),
    },
  })
  const payload = await response.json().catch(() => null) as AuthPayload | null
  if (!response.ok) throw new Error(payload?.error?.message || 'Unable to sign in. Please try again.')
  return payload?.data
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('checking')
  const [username, setUsername] = useState<string | null>(null)

  const checkSession = useCallback(async () => {
    try {
      const session = await authRequest('/api/auth/session')
      setUsername(session?.authenticated ? session.username ?? null : null)
      setStatus(session?.authenticated ? 'authenticated' : 'unauthenticated')
    } catch {
      setUsername(null)
      setStatus('unauthenticated')
    }
  }, [])

  useEffect(() => { void checkSession() }, [checkSession])
  useEffect(() => {
    const handleUnauthorized = () => {
      setUsername(null)
      setStatus('unauthenticated')
    }
    window.addEventListener('termovista:unauthorized', handleUnauthorized)
    return () => window.removeEventListener('termovista:unauthorized', handleUnauthorized)
  }, [])

  const login = useCallback(async (loginUsername: string, password: string) => {
    const session = await authRequest('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username: loginUsername, password }),
    })
    setUsername(session?.username ?? loginUsername.trim())
    setStatus('authenticated')
  }, [])

  const logout = useCallback(async () => {
    try {
      await authRequest('/api/auth/logout', { method: 'POST', body: JSON.stringify({}) })
    } finally {
      setUsername(null)
      setStatus('unauthenticated')
    }
  }, [])

  const value = useMemo(() => ({ status, username, login, logout }), [status, username, login, logout])
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside AuthProvider')
  return context
}
